import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { X, ExternalLink, Download, ShieldCheck } from 'lucide-react';
import { useContent } from '../hooks/useContent';
import { mergeCredentials } from '../data/credentialDefaults';

const ANNUAL_REPORTS = [
  { year: '2023–2024', href: 'https://drive.google.com/file/d/1Iv0GZRuoGgSA1BZTeAlcGuXdudFHywXR/view' },
  { year: '2022–2023', href: 'https://drive.google.com/file/d/1CElIdgx5ISZfxYbgScC_MiLXsE9dCbCd/view' },
  { year: '2021–2022', href: 'https://drive.google.com/file/d/1HTeidbv4OWdQGiHsjpBgJZBVAMGy6efL/view' },
  { year: '2020–2021', href: 'https://drive.google.com/file/d/1uHJ8V-i9UTi2_EzIlvmbNR6CFmVMoFOh/view' },
  { year: '2019–2020', href: 'https://drive.google.com/file/d/1-B95ca3vEUSwigdOVCBSC9iwuvwdWMMN/view' },
  { year: '2018–2019', href: 'https://drive.google.com/file/d/1-B95ca3vEUSwigdOVCBSC9iwuvwdWMMN/view' },
  { year: '2017–2018', href: 'https://drive.google.com/file/d/1BMdJvdHsU5jlNVLZldEGez_RY1kVa8WT/view' },
  { year: '2016–2017', href: 'https://drive.google.com/file/d/1qj1R9mqorelKMiZmuPc-kAZ2xkLhBk9C/view' },
  { year: '2015–2016', href: 'https://drive.google.com/file/d/1xU5rczUYUSKzgEYNdEOR86ERZgjo8T2z/view' },
  { year: '2014–2015', href: 'https://drive.google.com/file/d/1MK0caAeHNAUDxCHQetaLrsOTN0qoUTun/view' },
  { year: '2013–2014', href: 'https://drive.google.com/file/d/1eGFGMw7v0DHwfOW7AsvO9E_JLdLz3aYM/view' },
];

const NGO_FACTS = [
  { label: 'Reg No.', value: 'PPM-001-14-02011979', mono: true },
  { label: 'Registered', value: '1 February 1979' },
  { label: 'Legal Form', value: 'Non-Profit NGO / Yayasan' },
  { label: 'Jurisdiction', value: 'Kuala Lumpur, Malaysia' },
];

const Extras = ({ credKey }) => {
  if (credKey === 'registered-ngo') {
    return (
      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        {NGO_FACTS.map((f) => (
          <div key={f.label} className="bg-blue-50 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-black">{f.label}</div>
            <div className={`font-bold text-[#0A1628] mt-1 ${f.mono ? 'font-mono' : ''}`}>{f.value}</div>
          </div>
        ))}
      </div>
    );
  }
  if (credKey === 'tax-exempt') {
    return (
      <div className="mt-4 bg-emerald-50 border-l-4 border-emerald-500 rounded-r-lg p-4">
        <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-black">How to claim</div>
        <ol className="mt-2 space-y-1 text-sm text-gray-700 list-decimal ml-5">
          <li>Donate via any of our official channels</li>
          <li>Receive your official tax-exempt receipt within 3–5 working days</li>
          <li>Attach the receipt when you file your income tax (Borang B/BE)</li>
        </ol>
      </div>
    );
  }
  if (credKey === 'annual-audits') {
    return (
      <div className="mt-4">
        <a
          href="/annual-reports"
          className="flex items-center justify-between gap-2 bg-gradient-to-r from-[#0A1628] to-[#0F2440] text-white rounded-xl px-4 py-3 mb-4 hover:brightness-110 transition-all"
          data-testid="open-report-library-link"
        >
          <span className="text-sm font-bold">📖 Read reports online — interactive reader</span>
          <ExternalLink className="w-4 h-4 text-[#D4AF37]" />
        </a>
        <div className="text-[10px] uppercase tracking-widest text-gray-500 font-black mb-3">
          Download past annual reports
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {ANNUAL_REPORTS.map((r) => (
            <a
              key={r.year}
              href={r.href}
              target="_blank"
              rel="noreferrer noopener"
              className="flex items-center justify-between gap-2 bg-white border border-gray-200 hover:border-[#1565C0] hover:bg-blue-50 rounded-lg px-3 py-2 text-sm font-semibold text-[#0A1628] transition-colors"
              data-testid={`annual-report-${r.year.replace(/[–\/]/g, '-')}`}
            >
              <span className="font-mono">{r.year}</span>
              <Download className="w-3.5 h-3.5 text-[#1565C0]" />
            </a>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

export const CredentialModal = ({ credentialKey, onClose }) => {
  const { data } = useContent('credentials', {});
  const items = mergeCredentials(data?.items);
  const c = credentialKey ? items.find((i) => i.key === credentialKey) : null;

  return (
    <Dialog open={!!c} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden border-0" aria-describedby={undefined}>
        <DialogHeader className="bg-gradient-to-r from-[#0A1628] to-[#0F2440] text-white p-6 relative">
          <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent"></div>
          <DialogTitle className="text-2xl font-serif" data-testid="credential-modal-title">
            {c?.modal_title || c?.title}
          </DialogTitle>
          {(c?.modal_subtitle || c?.subtitle) && (
            <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-black mt-1">
              {c.modal_subtitle || c.subtitle}
            </div>
          )}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-full p-2 hover:bg-white/10 transition-colors"
            data-testid="credential-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </DialogHeader>
        <div className="p-6 max-h-[70vh] overflow-y-auto text-gray-700 leading-relaxed" data-testid="credential-modal-body">
          {(c?.body || '').split(/\n+/).filter(Boolean).map((para, i) => (
            <p key={i} className={i > 0 ? 'mt-3' : ''}>{para}</p>
          ))}
          {Array.isArray(c?.bullets) && c.bullets.length > 0 && (
            <ul className="mt-4 space-y-2 text-sm">
              {c.bullets.map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          )}
          {c && <Extras credKey={c.key} />}
          <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
            <a
              href="https://ewrf.org.my/resources/"
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-[#1565C0] hover:text-orange-600 transition-colors"
            >
              View all resources <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <a
              href="mailto:admin@ewrf.org.my"
              className="text-xs text-gray-500 hover:text-[#1565C0] transition-colors"
            >
              Questions? admin@ewrf.org.my
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CredentialModal;
